import React from 'react';
import styled from 'styled-components';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { getMlbGamedayUrl } from '../../utils/assetUrls';

const Wrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const GamedayLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 0.15rem;
  padding: 0.2rem 0.45rem;
  border-radius: 0.35rem;
  color: ${(props) => props.theme.primary};
  background: rgba(66, 153, 225, 0.12);
  border: 1px solid rgba(66, 153, 225, 0.35);
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease;
  &:hover {
    color: ${(props) => props.theme.primaryHover || props.theme.primary};
    background: rgba(66, 153, 225, 0.22);
    border-color: rgba(66, 153, 225, 0.55);
    box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.2);
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.primary};
    outline-offset: 2px;
  }
`;

/**
 * Three hollow diamonds (1st / 2nd / 3rd base layout). Uses currentColor for
 * stroke so light/dark mode follows surrounding text.
 */
function BasesDiamondIcon() {
  return (
    <svg
      width={14}
      height={11}
      viewBox="0 0 22 16"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <g fill="none" stroke="currentColor" strokeWidth={1.35} strokeLinejoin="miter">
        {/* Top — second base */}
        <path d="M 11 1.2 L 14.6 4.5 L 11 7.8 L 7.4 4.5 Z" />
        {/* Bottom left — third base */}
        <path d="M 5.2 8.4 L 8.8 11.7 L 5.2 15 L 1.6 11.7 Z" />
        {/* Bottom right — first base */}
        <path d="M 16.8 8.4 L 20.4 11.7 L 16.8 15 L 13.2 11.7 Z" />
      </g>
    </svg>
  );
}

/**
 * Renders game status text with an optional link to MLB Gameday when gameId is present.
 * @param {Object} props
 * @param {string} [props.status] - Game status text
 * @param {number|string} [props.gameId] - MLB game ID for Gameday URL
 */
function StatusWithGamedayLink({ status, gameId }) {
  const gamedayUrl = getMlbGamedayUrl(gameId);
  return (
    <Wrapper>
      <span>{status || 'Unknown'}</span>
      {gamedayUrl && (
        <GamedayLink
          href={gamedayUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="View on MLB Gameday"
          aria-label="View game on MLB Gameday (opens in new tab)"
          onClick={(e) => e.stopPropagation()}
        >
          <BasesDiamondIcon />
          <FaExternalLinkAlt size={12} aria-hidden />
        </GamedayLink>
      )}
    </Wrapper>
  );
}

export default StatusWithGamedayLink;
